import React from 'react';
import { ArrowLeftIcon, ArrowRightIcon, InfoIcon } from 'lucide-react';
import { DayProfileChart } from './DayProfileChart';
import { PaybackChart } from './PaybackChart';
import { products } from '../../data/products';
import { images } from '../../data/images';
import type { Product } from '../../types/product';
import type { UsageProfile } from '../../types/scan';
import type { Advice } from '../../utils/energy';
import { dec, euro, num } from '../../utils/format';
interface StepAdviesProps {
  usage: UsageProfile;
  advice: Advice;
  recommended: Product;
  onSelectProduct: (id: string) => void;
  onBack: () => void;
  onNext: () => void;
}
export function StepAdvies({
  usage,
  advice,
  recommended,
  onSelectProduct,
  onBack,
  onNext
}: StepAdviesProps) {
  const product = advice.product;
  const lowReturn = advice.savingsTotal < 200;
  const payback = Number.isFinite(advice.paybackYears) ? `${dec(advice.paybackYears)} jaar` : '—';
  return <div className="space-y-6" data-id="element-480" data-mp="2esk3s3uv78d9">
      <div data-id="element-481" data-mp="376q9k4coylxm">
        <h1 className="text-3xl font-semibold tracking-tight text-ink sm:text-4xl" data-id="element-482" data-mp="376zwfj2fwcz3">
          {product.id === recommended.id ? 'Ons advies:' : 'Jouw keuze:'} de {product.name}
        </h1>
        <p className="mt-3 text-lg text-ink-soft" data-id="element-483" data-mp="2y61q2u4j3tdc">
          Voor {num(usage.annualUsage)} kWh per jaar
          {usage.hasSolar ? ` en ${usage.solarPanels} zonnepanelen` : ', zonder zonnepanelen'}
          {usage.contract === 'dynamisch' ? ', met dynamisch contract' : ''}.
        </p>
      </div>

      <div className="grid overflow-hidden rounded-3xl bg-forest text-canvas sm:grid-cols-[1fr_190px]" data-id="element-484" data-mp="3uw2egs0zk7u4">
        <div className="p-6 sm:p-8" data-id="element-485" data-mp="3gwt8el6huu3v">
          <div className="text-[14px] text-canvas/60" data-id="element-486" data-mp="3gvrnlozoet8y">Geschatte besparing na 2027</div>
          <div className="mt-1 flex items-baseline gap-2" data-id="element-487" data-mp="3lul1vjmseoa6">
            <span className="text-5xl font-semibold tracking-tight tabular-nums text-volt sm:text-6xl" data-id="element-488" data-mp="25he40wbygzyq">{euro(advice.savingsTotal)}</span>
            <span className="text-lg text-canvas/80" data-id="element-489" data-mp="2ahik4l8ybsof">per jaar</span>
          </div>
          <dl className="mt-8 grid grid-cols-2 gap-6 border-t border-canvas/15 pt-6" data-id="element-490" data-mp="g0s9gokiorcp">
            <div data-id="element-491" data-mp="b1fq4dsuu2ev">
              <dt className="text-[13px] text-canvas/60" data-id="element-492" data-mp="b0w70trgx7z9">Terugverdientijd</dt>
              <dd className="mt-1 text-2xl font-semibold tabular-nums" data-id="element-493" data-mp="6a7edb41y4yj">{payback}</dd>
            </div>
            <div data-id="element-494" data-mp="293wyk6cw0v7">
              <dt className="text-[13px] text-canvas/60" data-id="element-495" data-mp="29nhm8ne53cx">Zelfvoorzienend</dt>
              <dd className="mt-1 text-2xl font-semibold tabular-nums" data-id="element-496" data-mp="3tevonnzs7lk4">
                <span className="text-canvas/50" data-id="element-497" data-mp="3td2hm7l9azbl">{Math.round(advice.autarkyWithout)}%</span> → {Math.round(advice.autarkyWith)}%
              </dd>
            </div>
          </dl>
        </div>
        <img src={images.product} alt="" className="hidden h-full w-full object-cover sm:block" data-id="element-498" data-mp="3jh92qhk2etqn" />
      </div>

      <div data-id="element-499" data-mp="3fhqkxunjiys1">
        <h2 className="text-[15px] font-medium text-ink" data-id="element-500" data-mp="1hpm9088sgkqh">Vergelijk formaten</h2>
        <div role="radiogroup" aria-label="Kies een batterij" className="mt-3 grid grid-cols-3 gap-2" data-id="element-501" data-mp="1hni71vug9vsu">
          {products.map(p => {
          const active = p.id === product.id;
          return <button key={p.id} type="button" role="radio" aria-checked={active} onClick={() => onSelectProduct(p.id)} className={`flex flex-col items-start rounded-2xl border bg-white p-4 text-left transition-colors duration-150 ${active ? 'border-ink' : 'border-line hover:border-ink/40'}`} data-id="element-502" data-mp="2vjtahv23nyji">
                <span className="flex w-full items-center justify-between gap-2" data-id="element-503" data-mp="2hl36dvgv4yln">
                  <span className="whitespace-nowrap text-[16px] font-semibold text-ink" data-id="element-504" data-mp="2lmyg9ocv0kb1">{p.name}</span>
                  {p.id === recommended.id && <span className="h-2 w-2 shrink-0 rounded-full bg-volt-deep" aria-label="Aanbevolen" data-id="element-505" data-mp="2qncbu6x9y1s6" />}
                </span>
                <span className="text-[13px] text-ink-muted" data-id="element-506" data-mp="zbnxle3o9ib">{dec(p.capacity)} kWh</span>
                <span className="mt-3 text-[15px] font-medium tabular-nums text-ink" data-id="element-507" data-mp="5uhb0nj8nu7z">{euro(p.price)}</span>
              </button>;
        })}
        </div>
        <p className="mt-2 text-[13px] text-ink-muted" data-id="element-508" data-mp="3vmhnpdy3pfii">
          <span className="mr-1.5 inline-block h-2 w-2 rounded-full bg-volt-deep align-middle" aria-hidden="true" data-id="element-509" data-mp="dfuwvn3pm08x" />
          Aanbevolen: hoogste netto opbrengst over 15 jaar
        </p>
      </div>

      {lowReturn && <div className="flex gap-3 rounded-2xl border border-sun/60 bg-white p-4 text-[15px]" data-id="element-510" data-mp="ndhdce0npzv0">
          <InfoIcon className="mt-0.5 h-5 w-5 shrink-0 text-sun" aria-hidden="true" data-id="element-511" data-mp="iffw7ymskam6" />
          <p className="text-ink-soft" data-id="element-512" data-mp="x3u62timkzc8">
            <span className="font-medium text-ink" data-id="element-513" data-mp="x2sn82vdb8jy">Beperkt rendement in deze situatie.</span> Met zonnepanelen of een
            dynamisch contract levert een batterij flink meer op. Onze adviseur denkt graag met je mee.
          </p>
        </div>}

      <section aria-labelledby="breakdown-title" className="rounded-3xl border border-line bg-white p-5 sm:p-6" data-id="element-514" data-mp="115fiyev5tdmf">
        <h2 id="breakdown-title" className="text-lg font-semibold text-ink" data-id="element-515" data-mp="1kqpfy8da3wdk">Waar komt de besparing vandaan?</h2>
        <dl className="mt-4 divide-y divide-line text-[15px]" data-id="element-516" data-mp="1pqk6gat56jy3">
          <div className="flex items-baseline justify-between gap-4 py-3" data-id="element-517" data-mp="1pnxwy5moxud8">
            <dt className="text-ink-soft" data-id="element-518" data-mp="1bq9fz8vqv7dg">
              Eigen zonnestroom ’s avonds gebruiken
              <span className="block text-[13px] text-ink-muted" data-id="element-519" data-mp="1fqb6qmzxygdy">{num(Math.round(advice.stored))} kWh per jaar opgeslagen</span>
            </dt>
            <dd className="font-medium tabular-nums text-ink" data-id="element-520" data-mp="16r6axofrl15h">{euro(advice.savingsSelf)}</dd>
          </div>
          <div className="flex items-baseline justify-between gap-4 py-3" data-id="element-521" data-mp="s31xayvkjftk">
            <dt className="text-ink-soft" data-id="element-522" data-mp="x1moy9wr8r6e">
              Slim laden en ontladen op uurprijzen
              <span className="block text-[13px] text-ink-muted" data-id="element-523" data-mp="wzacuddohb19">
                {usage.contract === 'dynamisch' ? 'Met je dynamische contract' : 'Alleen bij een dynamisch contract'}
              </span>
            </dt>
            <dd className="font-medium tabular-nums text-ink" data-id="element-524" data-mp="2pqno1mffuh7s">{euro(advice.savingsTrading)}</dd>
          </div>
          <div className="flex items-baseline justify-between gap-4 py-3" data-id="element-525" data-mp="2kz5lwv1oial8">
            <dt className="font-medium text-ink" data-id="element-526" data-mp="2g01f2b288oy2">Totaal per jaar</dt>
            <dd className="text-lg font-semibold tabular-nums text-ink" data-id="element-527" data-mp="2fy86gq81zunf">{euro(advice.savingsTotal)}</dd>
          </div>
        </dl>
        <p className="mt-2 text-[13px] text-ink-muted" data-id="element-528" data-mp="38oq7zmt4pnyq">± {num(Math.round(advice.co2Kg))} kg minder CO₂ per jaar</p>
      </section>

      <DayProfileChart usage={usage} product={product} data-id="element-529" data-mp="33qyi0yc1obx6" />
      <PaybackChart advice={advice} data-id="element-530" data-mp="2zoa6ujzuysx1" />

      <p className="text-[13px] leading-relaxed text-ink-muted" data-id="element-531" data-mp="2zn8v0db0q2e9">
        Indicatieve berekening op basis van € 0,27/kWh, € 0,05 terugleververgoeding en gemiddelde opwek van{' '}
        340 kWh per paneel. De definitieve berekening maken we tijdens het adviesgesprek.
      </p>

      <div className="flex items-center justify-between pt-2" data-id="element-532" data-mp="1j9ipd249362i">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-2 px-2 py-3 text-[15px] font-medium text-ink-soft hover:text-ink" data-id="element-533" data-mp="1jc4wj0n8bjk7">
          <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-534" data-mp="3s5n7is1440q" /> Terug
        </button>
        <button type="button" onClick={onNext} className="inline-flex items-center gap-2 whitespace-nowrap rounded-xl bg-volt px-6 py-4 text-[16px] font-semibold text-ink transition-colors duration-150 hover:bg-volt-deep" data-id="element-535" data-mp="7sfquoscmicf">
          Plan gratis adviesgesprek
          <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-536" data-mp="mgsr3xygyncq" />
        </button>
      </div>
    </div>;
}